import { useEffect, useMemo, useState } from 'react'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Skeleton, Tag } from 'antd'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { approveStrategyVersion, getStrategyVersion, rejectStrategyVersion, getStrategyVersionBacktestList } from '@/api'
import type { BacktestDeatil, CreateEditStrategyVersion, StrategyVersion } from '@/types'
import { localToUTC } from '@/utils/filters'
import { useFeedback } from '@/utils/antdApp'
import './index.scss'
import { marketOptions } from '@/pages/static'

type ReviewVersionPageProps = {
    versionId?: number | null
    color?: string | null
    onBack?: () => void
    onEdit?: () => void
    onCompleted?: () => void
    updateValue?: (nextValue: Partial<CreateEditStrategyVersion>) => void
}

type TimelineItem = {
    title: string
    date: string
    active?: boolean
    done?: boolean
}

function getStatusMeta(status?: string) {
    if (status === 'draft') {
        return { label: '草稿', className: 'is-default' }
    }
    if (status === 'pending') {
        return { label: '待审批', className: 'is-warning' }
    }
    if (status === 'active') {
        return { label: '已激活', className: 'is-success' }
    }
    if (status === 'archived') {
        return { label: '已归档', className: 'is-default' }
    }
    return { label: '--', className: 'is-default' }
}

function getTimeline(version: StrategyVersion | null): TimelineItem[] {
    if (!version) {
        return []
    }

    return [
        {
            title: '创建草稿',
            date: localToUTC(version.created_at),
            done: true,
        },
        {
            title: '提交审批',
            date: version.submitted_at ? localToUTC(version.submitted_at) : '待完成',
            done: Boolean(version.submitted_at),
        },
        {
            title: '审批通过并激活',
            date: version.activated_at ? localToUTC(version.activated_at) : '待完成',
            active: version.status === 'draft',
            done: Boolean(version.activated_at),
        },
        {
            title: '归档',
            date: version.archived_at ? localToUTC(version.archived_at) : '待完成',
            done: Boolean(version.archived_at),
        },
    ]
}

export default function ReviewVersionPage({
    versionId: controlledVersionId,
    color: controlledColor,
    onBack,
    onEdit,
    onCompleted,
    updateValue,
}: ReviewVersionPageProps = {}) {
    const { message, modal } = useFeedback()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const versionId = controlledVersionId ?? Number(searchParams.get('version_id'))
    const color = controlledColor ?? searchParams.get('color')
    const accentColor = color ? decodeURIComponent(color) : undefined
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [version, setVersion] = useState<StrategyVersion | null>(null)
    const [rejectReason, setRejectReason] = useState('')
    const [backtestList, setBacktestList] = useState<BacktestDeatil[]>([])

    useEffect(() => {
        if (Number.isNaN(versionId)) {
            message.error('缺少版本信息')
            setLoading(false)
            return
        }

        getStrategyVersion(versionId)
            .then((data) => {
                setVersion(data)
                updateValue?.({
                    strategy_snapshot: data.strategy_snapshot,
                    focus_sectors: data.focus_sectors,
                    constraints: data.constraints,
                })
                if(data.status === 'active' || data.status === 'archived'){
                    getStrategyVersionBacktestList(versionId).then((res) => {
                        setBacktestList(res.items)
                    }).catch(() => {
                        message.error('加载关联回测列表失败')
                    })
                }
            }) 
            .catch(() => {
                message.error('加载版本详情失败')
            })
            .finally(() => {
                setLoading(false)
            })
    }, [versionId])

    const statusMeta = useMemo(() => getStatusMeta(version?.status), [version?.status])
    const timeline = useMemo(() => getTimeline(version), [version])

    const handleBack = () => {
        if (onBack) {
            onBack()
            return
        }
        if (version?.manager_id) {
            const nextParams = new URLSearchParams({ manager_id: String(version.manager_id) })
            if (color) {
                nextParams.set('color', color)
            }
            navigate(`/versionManage?${nextParams.toString()}`)
            return
        }
        navigate('/versionManage')
    }

    const handleApprove = async () => {
        setSubmitting(true)
        try {
            approveStrategyVersion(versionId).then((res) => {
                if(res){
                    onCompleted?.()
                    handleBack()
                }
            })
        } finally {
            setSubmitting(false)
        }
    }

    const handleReject = async () => {
        setSubmitting(true)
        try {
            modal.confirm({
                title: '驳回理由',
                okText: '驳回',
                cancelText: '取消',
                content: <Input.TextArea className="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={200} placeholder="请输入驳回理由" />,
                onOk: () => {
                    if(rejectReason.trim()){
                        rejectStrategyVersion(versionId, rejectReason).then((res) => {
                            if(res){
                                onCompleted?.()
                                handleBack()
                            }
                        }).catch(() => {
                            message.error('驳回版本失败')
                        }).finally(() => {
                            setSubmitting(false)
                        })
                    } else {
                        message.error('请输入驳回理由')
                    }
                },
                onCancel: () => {
                    setSubmitting(false)
                }
            })
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        const loadingContent = (
            <div className="review-version-page">
                <div className="page-header review-version-page__header">
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                        版本列表
                    </Button>
                </div>
                <div className="page-content review-version-page__content">
                    <Skeleton active paragraph={{ rows: 12 }} />
                </div>
            </div>
        )
        return loadingContent
    }

    if (!version) {
        const emptyContent = (
            <div className="review-version-page">
                <div className="page-header review-version-page__header">
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
                        版本列表
                    </Button>
                </div>
                <div className="page-content review-version-page__content review-version-page__empty">
                    <Empty description="暂无版本信息" />
                </div>
            </div>
        )
        return emptyContent
    }

    const content = (
        <div className="review-version-page">
            <div className="page-header review-version-page__header">
                <div className="review-version-page__header-left">
                    <Button
                        type="text"
                        className="review-version-page__back"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                    >
                        版本列表
                    </Button>
                    <span>/</span>
                    <span className="review-version-page__title">
                        <b style={{ color: accentColor }}>·</b> {version.version_no} · {version.name}
                    </span>
                    <Tag className={`text-10 is-round ${statusMeta.className}`}>{statusMeta.label}</Tag>
                    <Tag className="text-10 is-purple">{marketOptions[version.market as keyof typeof marketOptions]}</Tag>
                </div>
            </div>

            <div className="page-content review-version-page__content">
                <div className="review-version-page__body">
                    <section className="review-version-page__summary-card">
                        <div className="review-version-page__summary-label text-10">核心投资策略</div>
                        <div className="review-version-page__summary-text color-1 text-13">
                            {version.strategy_snapshot || '暂无策略描述'}
                        </div>
                    </section>

                    {
                        version.status === 'pending' && <div className="review-version-page__actions">
                            <Button
                                type="primary"
                                className="review-version-page__approve"
                                onClick={() => void handleApprove()}
                                loading={submitting}
                            >
                                ✓ 审批通过 · 激活版本
                            </Button>
                            <Button
                                danger
                                className="review-version-page__reject"
                                onClick={() => void handleReject()}
                                loading={submitting}
                            >
                                ✗ 驳回
                            </Button>
                        </div>
                    }

                    {
                        version.status === 'active' && (
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ color: 'var(--green)', marginBottom: 8 }}>✓ 当前激活版本，已可直接用于回测</div>
                                {onEdit && <Button onClick={onEdit}>编辑此版本</Button>}
                            </div>
                        )
                    }

                    {
                        version.status === 'archived' && (
                            <div style={{ marginBottom: 12 }}>
                                <div className="color-3 text-11" style={{ marginBottom: 8 }}>已归档版本，可直接修改，保存后仍保持归档</div>
                                {onEdit && <Button onClick={onEdit}>编辑此版本</Button>}
                            </div>
                        )
                    }

                    {
                        (version.focus_sectors.length > 0 || version.constraints.length > 0) &&
                         <section className="review-version-page__section">
                            <div className="review-version-page__section-title">策略约束边界</div>
                            <div className="review-version-page__sub-title text-12 color-3" style={{ marginBottom: 8 }}>关注板块</div>
                            <div className="review-version-page__constraints flex">
                                {version.focus_sectors.map((sector) => (
                                    <Tag
                                        key={sector}
                                        className="border-tag text-11"
                                    >
                                        {sector}
                                    </Tag>
                                ))}
                            </div>
                            <div className="review-version-page__sub-title text-12 color-3" style={{ marginTop: 12 , marginBottom: 8 }}>风险约束</div>
                            <div className="review-version-page__constraints">
                                {version.constraints.map((item, index) => (
                                    <div key={index} className="review-version-page__constraint-item flex flex-center">
                                        <span className="review-version-page__constraint-dot" />
                                        <Input
                                            value={item}
                                            readOnly
                                            placeholder="例如：单股持仓不超过20%、行业暴露不超过30%..."
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    } 

                    <section className="review-version-page__section">
                        <div className="review-version-page__section-title">版本状态历程</div>
                        <div className="review-version-page__timeline text-12">
                            {timeline.map((item) => (
                                <div key={item.title} className="review-version-page__timeline-item">
                                    <div
                                        className={`review-version-page__timeline-dot ${item.done ? 'is-done' : ''} ${item.active ? 'is-active' : ''}`}
                                    >
                                        ✓
                                    </div>
                                    <div className="review-version-page__timeline-content">
                                        <div className="review-version-page__timeline-title">{item.title}</div>
                                        <div className="review-version-page__timeline-date text-11">{item.date}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {version.status !== 'draft' && version.status !== 'pending' && <section className="review-version-page__section">
                        <div className="review-version-page__section-title">关联回测记录</div>
                        {backtestList.length > 0 ? (
                            backtestList.map((item) => (
                                <div className='version-backtest-card flex items-center flex-between' key={item.id}>
                                    <div className='version-backtest-card__left text-12'>
                                        <Tag className={`version-backtest-card__tag text-10 ${item.status === 1 ? 'is-success' : item.status === 2 ? 'is-error' : 'is-default'}`} variant='outlined'>{item.status === 1 ? '已完成' : item.status === 2 ? '失败' : '进行中'}</Tag>
                                        <span className='version-backtest-card__title'>{item.display_name}</span>
                                    </div>
                                    <div className='version-backtest-card__right flex items-center gap-12'>
                                        <span className='color-3 text-11'>{item.backtest_range_start} ~ {item.backtest_range_end}</span>
                                        <span className={`font-weight-700 ${item.final_cumulative_return_pct > 0 ? 'green' : 'red'}`}>{item.final_cumulative_return_pct > 0 ? '+ ' : ''}{(item.final_cumulative_return_pct ?? 0).toFixed(2)}%</span>
                                        <Button className='version-backtest-card__button text-12' size='small' onClick={() => navigate(`/backtests/backtest/${item.run_id}`)}>查看结果 →</Button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="review-version-page__plain-text color-3 text-11">暂无关联回测</div>
                        )}
                    </section>}
                </div>
            </div>
        </div>
    )
    return content
}
